import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackRetailerMarketingTrustpilotTestimonialsView } from "@faire/web-api--source/events/retailerMarketing/view/trustpilotTestimonials";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import React, { type JSX } from "react";
import styled from "styled-components";

interface IProps {
  rating: number;
}

/**
 * Threshold above which we want to show a 5 star rating.
 * https://support.trustpilot.com/hc/en-us/articles/201748946-TrustScore-and-star-rating-explained
 */
const FIVE_STAR_THRESHOLD = 4.8;
const HIDDEN_THRESHOLD = 4.3;

export const TrustPilotWidget: React.FC<IProps> = ({ rating }) => {
  const { strictLocalize } = useStrictLocalization();
  const isSSR = useIsSSR();
  const viewRef = useDoActionWhenVisible(
    trackRetailerMarketingTrustpilotTestimonialsView
  );
  const logoUrl =
    "https://cdn.faire.com/fastly/ada8dc96a97da8d62daa0f30db77b7cb94f42027500f36bcb78ad107fc09fd4d.png";

  const ratingUrl =
    rating >= FIVE_STAR_THRESHOLD
      ? "https://cdn.faire.com/fastly/029be93298cd3d5e5d8ef06c06387d3cf3c41506f4da5f87b59b628a8655e6b8.png"
      : "https://cdn.faire.com/fastly/ddf78fe5f8bd0d64d1156f6af68388551e44aeb902613bdeb544be786e55af9d.png";

  if (rating < HIDDEN_THRESHOLD) {
    return null;
  }

  return (
    <Row ref={viewRef} align="center">
      <Typography align="center">
        <StrictLocalMsg
          defaultMessage="Rated <b>‘Excellent’</b> on"
          description={{
            text: "Explaining that Faire is well-rated on Trustpilot",
          }}
          values={DEPRECATED_backwardsCompatibleValues({
            b: BoldFormatting,
          })}
        />
      </Typography>
      <Spacer size="1x" />
      <RatingImage
        alt={strictLocalize(
          {
            defaultMessage:
              "Widget showing Faire's TrustPilot score of {numberScore}",
            description: {
              text: "Accessible text to describe an image showing Faire's TrustPilot score",
            },
          },
          DEPRECATED_backwardsCompatibleValues({ numberScore: rating })
        )}
        width={120}
        height={24}
        src={optimizeImageUrl(
          IImage.build({
            width: 120,
            height: 24,
            url: ratingUrl,
          }),
          isSSR
        )}
      />
      <Spacer size={{ mobileAndAbove: 0, tabletAndAbove: "1x" }} />
      <img
        alt={strictLocalize({
          defaultMessage: "Image of the Trustpilot Logo",
          description: {
            text: "Accessible text to describe an image showing the Trustpilot logo.",
          },
        })}
        style={{
          width: "80px",
          height: "20px",
        }}
        src={optimizeImageUrl(
          IImage.build({
            width: 80,
            height: 20,
            url: logoUrl,
          }),
          isSSR
        )}
      />
    </Row>
  );
};

const BoldFormatting = (chunks: JSX.Element) => <b>{chunks}</b>;

const RatingImage = styled.img`
  ${responsiveMediaQueries.mobile} {
    display: none;
  }
`;
